import { Alert, Box } from "@mui/material";
import { useLayoutEffect, useRef, useSyncExternalStore } from "react";
import CommonImageComp from "../../../components/ui/CommonImageComp";
import { useThemeContext } from "../../../globalContext/global-context";
import { hostThemes } from "../../../theme/hostThemes";
import type { QuestionGroupHost } from "../../QuestionFrameShared";
import {
  SurveyAttemptContent,
  type SurveyHostProps,
} from "../shared/HostShared";
import { preferredResponseFlow } from "../shared/responseFlow";
import {
  FormsCompactCompletionLayout,
  FormsWideCompletionLayout,
} from "./FormsCompletionLayout";
import { FormsQuestionFrame } from "./FormsQuestionFrame";
import {
  FormsQuestionGroupFrame,
  FormsQuestionGroupItemFrame,
} from "./FormsQuestionGroupFrame";

const formsQuestionGroupHost = {
  Frame: FormsQuestionGroupFrame,
  ItemFrame: FormsQuestionGroupItemFrame,
} satisfies QuestionGroupHost;

export const FormsSurveyHost = ({
  session,
  progress,
  arrangement,
  capabilities,
  view,
  onInspect,
  onRedirect,
  draftState,
  setDraftState,
}: SurveyHostProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const colors = theme.tokens.color;
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const attempt = snapshot.attempt;
  const scrollRef = useRef<HTMLDivElement>(null);
  const clientLogo = theme.assets.clientLogo?.trim();
  const contentTopInset = clientLogo
    ? theme.tokens.spacing["4"] +
      hostThemes.forms.canvas.clientLogoMaxHeight +
      theme.tokens.spacing["3"]
    : theme.tokens.spacing["5"];

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [attempt?.id]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        data-testid="forms-header-branding"
        sx={{
          alignItems: "center",
          display: "flex",
          gap: `${theme.tokens.spacing["3"]}px`,
          justifyContent: clientLogo ? "space-between" : "flex-end",
          left: `${theme.tokens.spacing["1"]}px`,
          minWidth: 0,
          pointerEvents: "none",
          position: "absolute",
          right: `${theme.tokens.spacing["4"]}px`,
          top: `${theme.tokens.spacing["4"]}px`,
          zIndex: 2,
        }}
      >
        {clientLogo && (
          <Box
            data-testid="client-logo-container"
            sx={{
              flexShrink: 1,
              height: hostThemes.forms.canvas.clientLogoMaxHeight,
              minWidth: 0,
              width: hostThemes.forms.canvas.clientLogoMaxWidth,
            }}
          >
            <CommonImageComp
              currentVal={{
                image: new URL(clientLogo, globalThis.location.origin).href,
                alt_text: theme.copy.labels.clientLogo,
              }}
              imageTestid="client-logo"
              disablePreviewWrapper
              fillContainer
            />
          </Box>
        )}
        {progress}
      </Box>
      <Box
        key={attempt?.id ?? "idle"}
        ref={scrollRef}
        data-testid="session-host-forms"
        sx={{
          height: "100%",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: "auto",
          position: "relative",
          backgroundColor: colors.surface.canvas,
          backgroundImage: `url(${new URL(theme.assets.chat.background, globalThis.location.origin).href})`,
          color: colors.text.primary,
          paddingInline: {
            xs: `${hostThemes.forms.canvas.compactInset}px`,
            md: `${hostThemes.forms.canvas.wideInset}px`,
          },
        }}
      >
        <Box
          data-testid="chat-main-container"
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            height: "100%",
            boxSizing: "border-box",
            paddingTop: `${contentTopInset}px`,
            paddingBottom: `${theme.tokens.spacing["5"]}px`,
          }}
        >
          {snapshot.error && (
            <Alert severity="error">{snapshot.error.message}</Alert>
          )}
          <SurveyAttemptContent
            session={session}
            snapshot={snapshot}
            Frame={FormsQuestionFrame}
            GroupFrame={formsQuestionGroupHost}
            preferredResponseFlow={preferredResponseFlow("forms")}
            completionLayout={
              theme.layout.orientation === "landscape"
                ? FormsWideCompletionLayout
                : FormsCompactCompletionLayout
            }
            arrangement={arrangement}
            capabilities={capabilities}
            view={view}
            onInspect={onInspect}
            onRedirect={onRedirect}
            draftState={draftState}
            setDraftState={setDraftState}
          />
        </Box>
      </Box>
    </Box>
  );
};
